/**
 * Poisson distribution helpers for the Brand Health threshold histogram.
 * DISPLAY ONLY — not used for any verdict decision (decisions come from the
 * snapshot, computed by the metric_pair_watch job). Mirrors the exact
 * algorithm from pair_watch.py::poisson_cdf for visual consistency.
 *
 * Ported verbatim from products/x2mail-admin/src/lib/poisson.ts.
 */

/** P(X = k) for Poisson(mu). Returns 0 for k < 0. */
export function poissonPmf(k: number, mu: number): number {
  if (k < 0) return 0;
  if (mu === 0) return k === 0 ? 1 : 0;
  // exp(-mu) * mu^k / k! — computed iteratively to avoid overflow
  let prob = Math.exp(-mu);
  for (let i = 1; i <= k; i++) {
    prob = (prob * mu) / i;
  }
  return prob;
}

/** P(X <= g) for Poisson(mu). Exact iterative sum matching pair_watch.py. */
export function poissonCdf(g: number, mu: number): number {
  if (g < 0) return 0;
  if (mu === 0) return 1;
  let prob = Math.exp(-mu);
  let cumsum = prob;
  for (let k = 1; k <= g; k++) {
    prob = (prob * mu) / k;
    cumsum += prob;
  }
  return Math.min(cumsum, 1);
}

export interface PoissonHistogramBar {
  k: number;
  pmf: number;
  isObs: boolean;
  isCritOrBelow: boolean;
}

/**
 * Returns PMF values for k=0..maxK for histogram bars.
 * maxK defaults to max(g_crit + 8, 15) to show context around the threshold.
 */
export function poissonHistogramData(
  mu: number,
  gObs: number,
  gCrit: number
): PoissonHistogramBar[] {
  const maxK = Math.max(gCrit + 8, gObs + 4, 15);
  const result: PoissonHistogramBar[] = [];
  for (let k = 0; k <= maxK; k++) {
    result.push({
      k,
      pmf: poissonPmf(k, mu),
      isObs: k === gObs,
      isCritOrBelow: gCrit >= 0 && k <= gCrit,
    });
  }
  return result;
}
